// pages/master/role/content/MenuManage.jsx
'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import { updateAllMenuForRole } from '../../../../stores/roleStore';
import { useNotifier } from '../../../../components/Notifier';
import { useLanguage } from '../../../../utils/i18n';
import FormDialog from '../../../../components/FormDialog';

const EditFeaturesDialog = ({ open, onClose, nodeData, onSaveSingle }) => {
  const { t } = useLanguage();
  const getInitialStatus = (featName) => {
    if (!nodeData || !nodeData.features) return false;
    const f = nodeData.features.find((x) => String(x.name).toLowerCase() === featName.toLowerCase());
    return f ? f.status : false;
  };

  const [features, setFeatures] = useState({
    read: getInitialStatus('read'),
    create: getInitialStatus('create'),
    update: getInitialStatus('edit'),
    delete: getInitialStatus('delete'),
    report: getInitialStatus('report'),
    active: nodeData?.is_active ?? false
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (event) => setFeatures({ ...features, [event.target.name]: event.target.checked });

  const handleSave = async () => {
    setSaving(true);
    await onSaveSingle(nodeData.id, features);
    setSaving(false);
    onClose();
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={`${t('masterRole.editPrivileges', 'Edit Privileges')}: ${nodeData?.alias || '-'}`}
      maxWidth="xs"
      actions={
        <>
          <Button onClick={onClose} sx={{ color: 'text.secondary' }}>
            {t('general.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" color="success" onClick={handleSave} disabled={saving} disableElevation>
            {saving ? t('general.processing', 'Processing...') : t('general.save', 'Save')}
          </Button>
        </>
      }
    >
      <Box sx={{ pt: 1, pb: 2, display: 'flex', flexDirection: 'column' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={features.read}
              onChange={handleChange}
              name="read"
              sx={{ color: '#2196f3', '&.Mui-checked': { color: '#2196f3' } }}
            />
          }
          label="Read"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={features.create}
              onChange={handleChange}
              name="create"
              sx={{ color: '#4caf50', '&.Mui-checked': { color: '#4caf50' } }}
            />
          }
          label="Create"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={features.update}
              onChange={handleChange}
              name="update"
              sx={{ color: '#ff9800', '&.Mui-checked': { color: '#ff9800' } }}
            />
          }
          label="Update"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={features.delete}
              onChange={handleChange}
              name="delete"
              sx={{ color: '#f44336', '&.Mui-checked': { color: '#f44336' } }}
            />
          }
          label="Delete"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={features.report}
              onChange={handleChange}
              name="report"
              sx={{ color: '#9c27b0', '&.Mui-checked': { color: '#9c27b0' } }}
            />
          }
          label="Report"
        />
        <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid #eee' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={features.active}
                onChange={handleChange}
                name="active"
                sx={{ color: '#607d8b', '&.Mui-checked': { color: '#607d8b' } }}
              />
            }
            label={<b>{t('general.activeModule', 'Active Module')}</b>}
          />
        </Box>
      </Box>
    </FormDialog>
  );
};

const MenuTreeNode = ({ node, level = 0, onEditSingle, isEditAll, isSearching, onFeatureChange, disableAllActions }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isEditAll || isSearching) setOpen(true);
  }, [isEditAll, isSearching]);

  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const handleToggle = () => {
    if (hasChildren) setOpen(!open);
  };

  const getFeatureStatus = (featName) => {
    if (!node.features) return false;
    const f = node.features.find((x) => String(x.name).toLowerCase() === featName.toLowerCase());
    return f ? f.status : false;
  };

  const handleCheckChange = (featName, checked) => {
    if (!isEditAll) return;
    onFeatureChange(node.id, featName, checked);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.5,
          px: 2,
          borderBottom: '1px solid #f0f0f0',
          bgcolor: open && level === 0 ? '#f8f9fa' : 'transparent',
          '&:hover': { bgcolor: '#f4f6f8' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, pl: level * 4 }}>
          {hasChildren ? (
            <IconButton size="small" onClick={handleToggle} sx={{ mr: 1, color: 'text.secondary' }}>
              {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            </IconButton>
          ) : (
            <Box sx={{ width: 34, mr: 1 }} />
          )}
          <Typography variant="body1" fontWeight={hasChildren ? 600 : 400}>
            {node.alias || node.module_name}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {['read', 'create', 'edit', 'delete', 'report'].map((f) => {
            const getFeatureColor = (feat) => {
              switch (feat) {
                case 'read':
                  return '#2196f3';
                case 'create':
                  return '#4caf50';
                case 'edit':
                  return '#ff9800';
                case 'delete':
                  return '#f44336';
                case 'report':
                  return '#9c27b0';
                default:
                  return 'primary.main';
              }
            };
            return (
              <FormControlLabel
                key={f}
                control={
                  <Checkbox
                    size="small"
                    checked={getFeatureStatus(f)}
                    onChange={(e) => handleCheckChange(f, e.target.checked)}
                    disableRipple={!isEditAll}
                    sx={{
                      cursor: isEditAll ? 'pointer' : 'default',
                      color: getFeatureColor(f),
                      '&.Mui-checked': { color: getFeatureColor(f) }
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {f === 'edit' ? t('general.update', 'Update') : f}
                  </Typography>
                }
                sx={{ m: 0, pointerEvents: isEditAll ? 'auto' : 'none' }}
              />
            );
          })}

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={node.is_active}
                onChange={(e) => handleCheckChange('active', e.target.checked)}
                disableRipple={!isEditAll}
                sx={{
                  cursor: isEditAll ? 'pointer' : 'default',
                  color: '#607d8b',
                  '&.Mui-checked': { color: '#607d8b' }
                }}
              />
            }
            label={<Typography variant="body2">{t('general.active', 'Active')}</Typography>}
            sx={{ m: 0, pointerEvents: isEditAll ? 'auto' : 'none' }}
          />
        </Box>
      </Box>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {node.children.map((childNode) => (
              <MenuTreeNode
                key={childNode.id || childNode.module_name}
                node={childNode}
                level={level + 1}
                onEditSingle={onEditSingle}
                isEditAll={isEditAll}
                isSearching={isSearching}
                onFeatureChange={onFeatureChange}
                disableAllActions={disableAllActions}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const MenuManage = ({ roleName, dataModuleByName, roleParentName, onClose }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [menuItems, setMenuItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [localLoading, setLocalLoading] = useState(true);

  const isEditAll = true;
  const [isSavingAll, setIsSavingAll] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);

  const [disableAllActions, setDisableAllActions] = useState(true);

  const getFilteredTree = (nodes, query) => {
    if (!query.trim()) return nodes;
    const lowerQuery = query.toLowerCase();

    return nodes.reduce((acc, node) => {
      const filteredChildren = node.children ? getFilteredTree(node.children, query) : [];
      const isMatch = (node.alias || node.module_name || node.modules_name || '').toLowerCase().includes(lowerQuery);

      if (isMatch || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren
        });
      }
      return acc;
    }, []);
  };

  const displayedMenuItems = getFilteredTree(menuItems, searchQuery);
  const isSearching = !!searchQuery.trim();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedRoleName = localStorage.getItem('rolename');
      if (storedRoleName && roleParentName && storedRoleName === roleParentName) {
        setDisableAllActions(false);
      }
    }
  }, [roleParentName]);

  useEffect(() => {
    setLocalLoading(true);
    const timer = setTimeout(() => {
      const rootMenuData = dataModuleByName?.data_modules || dataModuleByName?.data || dataModuleByName;
      const treeItems = Array.isArray(rootMenuData) ? rootMenuData : rootMenuData ? [rootMenuData] : [];

      if (treeItems.length > 0) {
        setMenuItems(JSON.parse(JSON.stringify(treeItems)));
      } else {
        setMenuItems([]);
      }
      setLocalLoading(false);
    }, 150);

    return () => clearTimeout(timer);
  }, [dataModuleByName]);
  // ==========================

  const updateNodeInTree = (nodes, id, featName, newStatus) => {
    return nodes.map((node) => {
      if (node.id === id) {
        if (featName === 'active') {
          node.is_active = newStatus;
        } else {
          const featIndex = node.features.findIndex((f) => String(f.name).toLowerCase() === featName.toLowerCase());
          if (featIndex > -1) {
            node.features[featIndex].status = newStatus;
          }
        }
        node._isModified = true;
        return { ...node };
      }

      if (node.children && node.children.length > 0) {
        node.children = updateNodeInTree(node.children, id, featName, newStatus);
      }

      return node;
    });
  };

  const handleFeatureChange = useCallback((id, featName, checked) => {
    setMenuItems((prev) => updateNodeInTree(prev, id, featName, checked));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveSingle = useCallback((id, features) => {
    setMenuItems((prev) => {
      let updated = prev;
      Object.entries(features).forEach(([featName, status]) => {
        const targetFeat = featName === 'update' ? 'edit' : featName;
        updated = updateNodeInTree(updated, id, targetFeat, status);
      });
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAll = async () => {
    setIsSavingAll(true);
    try {
      const payload = {
        name: roleName,
        data_modules: menuItems
      };

      await updateAllMenuForRole(payload);
      notify({ message: t('masterRole.updateMenuSuccess', 'Menu access successfully updated'), severity: 'success' });
      if (onClose) onClose();
    } catch (err) {
      console.error(err);
      notify({ message: t('masterRole.updateMenuFailed', 'Failed to update menu access'), severity: 'error' });
    } finally {
      setIsSavingAll(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: '#fff',
          py: 1.5
        }}
      >
        <Typography variant="h6" color="primary" fontWeight="bold">
          {t('masterRole.menuFeaturesSetup', 'Menu & Features Setup')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder={t('masterRole.searchMenu', 'Search menu...')}
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 250, bgcolor: '#fff', borderRadius: 1, '& .MuiInputBase-root': { height: 40 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')} edge="end">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
          <Button
            variant="contained"
            color="info"
            startIcon={isSavingAll ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSaveAll}
            disabled={isSavingAll}
            disableElevation
            sx={{ height: 40, textTransform: 'none' }}
          >
            {isSavingAll ? t('general.processing', 'Processing...') : t('general.saveAllChanges', 'Save All Changes')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ width: '100%', bgcolor: '#fff', borderRadius: 1, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        {localLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress />
          </Box>
        ) : displayedMenuItems.length > 0 ? (
          displayedMenuItems.map((item) => (
            <MenuTreeNode
              key={item.id || item.module_name}
              node={item}
              onEditSingle={(node) => {
                setSelectedNode(node);
                setEditDialogOpen(true);
              }}
              isEditAll={isEditAll}
              isSearching={isSearching}
              onFeatureChange={handleFeatureChange}
              disableAllActions={disableAllActions}
            />
          ))
        ) : (
          <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>
              {searchQuery
                ? t('masterRole.noMenuMatch', 'No matching menu found.')
                : t('masterRole.noMenuData', 'No menu module data for this role.')}
            </Typography>
          </Box>
        )}
      </Box>

      {editDialogOpen && (
        <EditFeaturesDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedNode(null);
          }}
          nodeData={selectedNode}
          onSaveSingle={handleSaveSingle}
        />
      )}
    </Box>
  );
};

export default MenuManage;
